import { forwardRef, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Eye, EyeOff } from "lucide-react";
import { YMark } from "@/components/Brand";

/**
 * Shared UI primitives for the ported iOS (auth) screens.
 * Every measurement here comes directly from the iOS `kit.tsx`, `otp.tsx`,
 * and per-screen `styles` blocks so the web UI reads identically.
 */

// Exact rainbow order from iOS `theme.ts` — matches the auth-landing pill.
export const BRONTO_RAINBOW =
  "linear-gradient(90deg, #FFD600 0%, #FF7A00 25%, #FF0069 50%, #D300C5 75%, #7638FA 100%)";

/* -----------------------------------------------------------------------
 * AuthShell — top bar with a rounded 40px back button + centred Y hero.
 * Every email-auth screen (login / signup / verify / forgot / reset)
 * uses this shell so the vertical rhythm is 1:1 with iOS.
 * ----------------------------------------------------------------------- */
export function AuthShell({
  children,
  testId,
  onBack,
  title,
  subtitle,
  yHeight = 44,
}) {
  const navigate = useNavigate();
  const handleBack = onBack || (() => (window.history.length > 1 ? navigate(-1) : navigate("/")));

  return (
    <div
      className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col"
      data-testid={testId}
    >
      {/* Top bar — 52px, back button 40x40 rounded */}
      <div className="h-[52px] flex items-center px-2 shrink-0">
        <button
          type="button"
          onClick={handleBack}
          className="w-10 h-10 rounded-full flex items-center justify-center text-foreground hover:bg-secondary active:opacity-85 transition-all"
          aria-label="Back"
          data-testid={`${testId}-back`}
        >
          <ChevronLeft size={25} strokeWidth={2.4} />
        </button>
      </div>

      {/* Scroll container — iOS paddingHorizontal 24, paddingTop 16, paddingBottom 48 */}
      <div className="flex-1 overflow-y-auto px-6 pt-4 pb-12">
        <div className="mx-auto w-full max-w-[440px]">
          {/* Y hero */}
          <div className="flex justify-center">
            <YMark height={yHeight} testId={`${testId}-y-mark`} />
          </div>

          {/* Title + subtitle block — iOS marginTop 32, 29px title with -0.7 tracking */}
          {(title || subtitle) && (
            <div className="text-center mt-8">
              {title && (
                <h1
                  className="font-jakarta font-extrabold text-foreground"
                  style={{
                    fontSize: 29,
                    letterSpacing: "-0.7px",
                    lineHeight: 1.15,
                  }}
                  data-testid={`${testId}-title`}
                >
                  {title}
                </h1>
              )}
              {subtitle && (
                <p
                  className="text-muted-foreground"
                  style={{ fontSize: 16, marginTop: 10, lineHeight: "22px" }}
                  data-testid={`${testId}-subtitle`}
                >
                  {subtitle}
                </p>
              )}
            </div>
          )}

          {/* Screen body */}
          {children}
        </div>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Primary rainbow "Button" — iOS `kit.tsx` Button variant="primary".
 * Height 52 (h-13), rainbow gradient, white bold text.
 * ----------------------------------------------------------------------- */
export function PrimaryButton({
  label,
  onClick,
  loading = false,
  disabled = false,
  type = "button",
  className = "",
  testId,
}) {
  return (
    <button
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      className={`h-[52px] rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.99] active:opacity-90 hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{ background: BRONTO_RAINBOW }}
      data-testid={testId}
    >
      {loading && <Loader2 size={16} className="animate-spin text-white" />}
      <span className="text-white font-bold text-[16px] tracking-[-0.01em]">{label}</span>
    </button>
  );
}

/* -----------------------------------------------------------------------
 * TextField — iOS `kit.tsx` TextField.
 * secondaryBg surface, 14px radius, 16px inner font, hairline border,
 * optional label (13px uppercase secondary), optional error text.
 * ----------------------------------------------------------------------- */
export const TextField = forwardRef(function TextField(
  {
    label,
    error,
    inputClassName = "",
    className = "",
    rightAdornment,
    leftAdornment,
    testId,
    ...inputProps
  },
  ref
) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <label
          className="text-muted-foreground font-semibold"
          style={{ fontSize: 13, letterSpacing: "0.02em" }}
          htmlFor={inputProps.id}
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftAdornment && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none text-muted-foreground">
            {leftAdornment}
          </div>
        )}
        <input
          ref={ref}
          className={`w-full h-[52px] bg-secondary rounded-[14px] px-4 text-[16px] text-foreground placeholder:text-muted-foreground border transition-colors focus:outline-none focus:border-foreground/25 ${
            error ? "border-destructive" : "border-border"
          } ${leftAdornment ? "pl-10" : ""} ${rightAdornment ? "pr-12" : ""} ${inputClassName}`}
          data-testid={testId}
          {...inputProps}
        />
        {rightAdornment && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            {rightAdornment}
          </div>
        )}
      </div>
      {error && (
        <p className="text-destructive text-xs" data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
});

/* -----------------------------------------------------------------------
 * ErrorBox — the soft red inline error banner used across auth screens.
 * ----------------------------------------------------------------------- */
export function ErrorBox({ children, testId }) {
  return (
    <div
      className="rounded-[14px] px-3.5 py-3 border border-destructive"
      style={{ backgroundColor: "rgba(255, 59, 48, 0.08)" }}
      data-testid={testId}
    >
      <span className="text-destructive text-sm">{children}</span>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * OtpBoxes — 6-cell OTP entry, iOS spec:
 *   • Each cell 60×60, secondaryBg fill, borderRadius 14
 *   • Filled OR active cell → 2px brand-purple border (`#7638FA` / `#9B6BFF`)
 *   • Empty resting cell → hairline `theme.border`
 *   • Digit rendered 26px weight 700, centered
 * Mirrors iOS `otp.tsx`: individual boxes, autofocus on the first empty
 * cell, backspace jumps to previous cell, paste populates all cells,
 * calls `onComplete(v)` when the last cell is filled.
 * ----------------------------------------------------------------------- */
export function OtpBoxes({
  length = 6,
  value,
  onChange,
  onComplete,
  autoFocus = true,
  testId,
}) {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus) {
      const firstEmpty = digits.findIndex((d) => !d);
      const idx = firstEmpty === -1 ? length - 1 : firstEmpty;
      refs.current[idx]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAt = useCallback(
    (idx, ch) => {
      const arr = digits.slice();
      arr[idx] = ch;
      const next = arr.join("").slice(0, length);
      onChange(next);
      if (next.length === length && onComplete) onComplete(next);
    },
    [digits, length, onChange, onComplete]
  );

  const handleChange = (idx, raw) => {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) return setAt(idx, "");
    if (cleaned.length > 1) {
      // Handle paste of multiple digits
      const merged = (value + cleaned).replace(/\D/g, "").slice(0, length);
      onChange(merged);
      if (merged.length === length && onComplete) onComplete(merged);
      refs.current[Math.min(merged.length, length - 1)]?.focus();
      return;
    }
    setAt(idx, cleaned);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        setAt(idx, "");
      } else if (idx > 0) {
        setAt(idx - 1, "");
        refs.current[idx - 1]?.focus();
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      refs.current[idx - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      refs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (idx, e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    if (pasted.length === length && onComplete) onComplete(pasted);
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  // Focused-cell index — used to draw the brand ring on the caret.
  const focusedIndex = Math.min(value.length, length - 1);

  return (
    <div className="flex items-center justify-between gap-2" data-testid={testId}>
      {Array.from({ length }).map((_, idx) => {
        const filled = !!digits[idx];
        const active = idx === focusedIndex && value.length < length;
        const brand = filled || active;
        return (
          <input
            key={idx}
            ref={(el) => (refs.current[idx] = el)}
            type="tel"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digits[idx]}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={(e) => handlePaste(idx, e)}
            className={`flex-1 h-[60px] rounded-[14px] bg-secondary text-center text-[26px] font-bold text-foreground focus:outline-none caret-transparent transition-colors ${
              brand ? "border-[2px]" : "border"
            }`}
            style={{
              borderColor: brand ? "#7638FA" : "hsl(var(--border))",
              maxWidth: 60,
            }}
            data-testid={testId ? `${testId}-${idx}` : undefined}
          />
        );
      })}
    </div>
  );
}

/* -----------------------------------------------------------------------
 * EyeToggle — password visibility button, matches iOS `eyeBtn` position.
 * ----------------------------------------------------------------------- */
export function EyeToggle({ show, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="p-1 text-muted-foreground hover:text-foreground transition-colors"
      aria-label={show ? "Hide password" : "Show password"}
      data-testid={testId}
    >
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );
}
